<template>
  <q-page class="bg-page q-pa-xl">
    <div class="container-xl">

      <!-- ПРЕГЛЕДИ -->
      <div v-if="mode === 'pregledi'">
        <!-- Заглавје -->
        <div class="header-section q-mb-lg flex justify-between items-center">
          <div class="flex items-center">
            <h5 class="text-mint text-bold q-mb-none">Преглед на сите барања без фази</h5>
          </div>

          <q-btn
            icon="file_download"
            label="Excel"
            outline
            color="mint"
            class="rounded-10 text-bold"
            no-caps
            @click="exportExcel"
          />
        </div>

        <!-- Инфо панел -->
        <div class="info-strip q-mb-lg shadow-1">
          <div class="info-item">
            <q-icon name="person" size="18px" class="text-mint q-mr-xs" />
            <b>{{ userFullName }}</b>
          </div>

          <div class="info-item">
            <q-icon name="event" size="18px" class="text-mint q-mr-xs" />
            {{ formatDate(new Date()) }}
          </div>

          <div class="info-item">
            <q-icon name="list_alt" size="18px" class="text-mint q-mr-xs" />
            Вкупно барања: <b>{{ filtrirani.length }}</b>
          </div>
        </div>

        <!-- Филтри -->
        <q-card flat bordered class="filter-card q-pa-md q-mb-lg">
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-3">
              <q-input
                v-model="filtersDraft.from"
                type="date"
                label="Од датум"
                outlined
                dense
                color="mint"
              />
            </div>

            <div class="col-12 col-md-3">
              <q-input
                v-model="filtersDraft.to"
                type="date"
                label="До датум"
                outlined
                dense
                color="mint"
              />
            </div>

            <div class="col-12 col-md-3">
              <q-select
                v-model="filtersDraft.status"
                :options="statusOptions"
                option-label="label"
                option-value="value"
                label="Статуси"
                outlined
                dense
                color="mint"
                emit-value
                map-options
              />
            </div>

            <div class="col-12 col-md-3">
              <q-select
                v-model="filtersDraft.employee"
                :options="employeeOptions"
                label="Вработени"
                outlined
                dense
                color="mint"
                emit-value
                map-options
              />
            </div>
          </div>

          <div class="btn-actions q-mt-md flex justify-center q-gutter-sm">
            <q-btn
              label="Исчисти"
              outline
              color="mint"
              rounded
              no-caps
              class="btn-filter btn-filter-outline"
              @click="resetFiltri"
            />

            <q-btn
              label="Примени"
              color="mint"
              unelevated
              rounded
              no-caps
              class="btn-filter"
              @click="primeniFiltri"
            />
          </div>
        </q-card>

        <!-- Табела барања -->
        <q-card flat bordered class="table-card q-pa-md">
          <div class="row items-center q-col-gutter-md q-mb-md">
            <div class="col-12 col-md-4">
              <q-input
                v-model="searchId"
                outlined
                dense
                color="mint"
                label="Пребарај по број "
                placeholder=""
                clearable
                maxlength="10"
                @update:model-value="onSearchIdInput"
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
              </q-input>
            </div>
          </div>

          <q-table
            :data="filtrirani"
            :columns="columns"
            row-key="ID"
            flat
            dense
            class="rounded-xl table-clean clickable-table"
            no-data-label="Нема податоци за прикажување."
            @row-click="openRequestPopup"
          >
            <template v-slot:body-cell-ID="props">
              <q-td align="center">
                {{ formatBrojID(props.row.ID) }}
              </q-td>
            </template>

            <template v-slot:body-cell-Datum="props">
              <q-td align="center">{{ formatDate(props.row.Datum) }}</q-td>
            </template>

            <template v-slot:body-cell-AplikacijaID="props">
              <q-td align="center">
                {{ props.row.OpisAplikacija }}
              </q-td>
            </template>

            <template v-slot:body-cell-PartnerID="props">
              <q-td align="left">
                {{ props.row.NazivPartner || "" }}
              </q-td>
            </template>

            <template v-slot:body-cell-Opis="props">
              <q-td align="left">
                <div class="opis-preview">
                  {{ shortText(props.row.Opis, 55) }}
                </div>
              </q-td>
            </template>

            <template v-slot:body-cell-StatusID="props">
              <q-td align="center">
                {{ statusiMap[props.row.StatusID] || props.row.StatusID }}
              </q-td>
            </template>

            <template v-slot:body-cell-Files="props">
              <q-td align="center" @click.stop>
                <q-icon
                  v-if="!props.row.Files || props.row.Files.length === 0"
                  name="attach_file"
                  color="grey-5"
                  size="22px"
                >
                  <q-tooltip>Нема прикачени фајлови</q-tooltip>
                </q-icon>

                <q-btn
                  v-else
                  flat
                  round
                  dense
                  color="mint"
                  icon="attach_file"
                >
                  <q-tooltip>{{ props.row.Files.length }} фајлови</q-tooltip>

                  <q-menu>
                    <q-list dense bordered>
                      <q-item
                        v-for="(file, idx) in props.row.Files"
                        :key="idx"
                        clickable
                        @click="previewFile(file)"
                      >
                        <q-item-section avatar>
                          <q-icon name="insert_drive_file" color="mint" />
                        </q-item-section>
                        <q-item-section>{{ file.split('/').pop() }}</q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </q-td>
            </template>

            <template v-slot:body-cell-akcii="props">
              <q-td align="center" @click.stop>
                <q-btn
                  flat
                  dense
                  round
                  color="mint"
                  icon="edit"
                  @click.stop="openRequestPopup(null, props.row)"
                >
                  <q-tooltip>Преглед / Следна фаза</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>
        </q-card>
      </div>

      <!-- СТАР ДЕЛ ФАЗИ - ОСТАВЕН -->
      <div v-if="mode === 'fazi'">
        <div class="close-top-right">
          <q-btn
            flat
            dense
            round
            color="mint"
            icon="close"
            @click="mode = 'pregledi'"
          />
        </div>

        <div class="text-h6 text-mint text-bold q-mb-lg text-center">
          Фази за барање {{ formatBrojID(selected.ID) }}
        </div>

        <q-card flat bordered class="q-pa-lg q-m-xl fazi-grid">
          <div class="row q-col-gutter-sm">
            <div class="col-12 col-md-2-4">
              <q-select
                class="fazi-small"
                v-model="newFaza.fazaID"
                :options="faziOptions"
                :label="fazaErrors.fazaID ? 'Задолжително поле' : 'Фаза'"
                outlined
                dense
                color="mint"
                emit-value
                map-options
                hide-bottom-space
                :class="{ 'req-field': fazaErrors.fazaID, 'required-field': !fazaErrors.fazaID }"
                @update:model-value="fazaErrors.fazaID = false"
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-select
                class="fazi-small"
                v-model="newFaza.predal"
                :options="[...employeeOptions]"
                :label="fazaErrors.predal ? 'Задолжително поле' : 'Предал'"
                outlined
                dense
                color="mint"
                emit-value
                map-options
                hide-bottom-space
                :class="{ 'req-field': fazaErrors.predal, 'required-field': !fazaErrors.predal }"
                @update:model-value="fazaErrors.predal = false"
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-select
                class="fazi-small"
                v-model="newFaza.primil"
                :options="employeeOptions"
                label="Примил"
                outlined
                dense
                color="mint"
                emit-value
                map-options
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model="newFaza.predvidenDatum"
                type="date"
                label="Предвиден датум"
                outlined
                dense
                color="mint"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model.number="newFaza.predvidenoVreme"
                type="number"
                step="0.25"
                min="0"
                label="Предвидено време"
                outlined
                dense
                color="mint"
                hide-bottom-space
              />
            </div>
          </div>

          <div class="row q-col-gutter-sm q-mt-sm">
            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model="newFaza.start"
                type="date"
                label="Почеток"
                outlined
                dense
                color="mint"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model="newFaza.end"
                type="date"
                label="Крај"
                outlined
                dense
                color="mint"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model.number="newFaza.casovi"
                type="number"
                step="0.25"
                min="0"
                label="Часови"
                outlined
                dense
                color="mint"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model.number="newFaza.efektivno"
                type="number"
                step="0.25"
                min="0"
                label="Ефективно"
                outlined
                dense
                color="mint"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-2-4">
              <q-input
                class="fazi-small"
                v-model="newFaza.zabeleska"
                label="Забелешка"
                outlined
                dense
                color="mint"
                type="textarea"
                autogrow
                hide-bottom-space
              />
            </div>
          </div>

          <div class="text-center q-mt-md">
            <q-btn
              label="Додади фаза"
              color="mint"
              unelevated
              rounded
              no-caps
              @click="dodadiFaza"
            />
          </div>
        </q-card>
      </div>

      <!-- POPUP ПРЕГЛЕД НА БАРАЊЕ -->
      <q-dialog v-model="requestDialog" persistent maximized-on-mobile>
        <q-card class="request-dialog-card">
          <q-card-section class="dialog-header">
            <div class="row items-center justify-between">
              <div class="text-h6 text-mint text-bold">
                Преглед на барање {{ formatBrojID(selected.ID) }}
              </div>

              <q-btn
                flat
                dense
                round
                icon="close"
                color="grey-7"
                @click="requestDialog = false"
              />
            </div>
          </q-card-section>

          <q-separator />

          <q-card-section class="dialog-content">
            <div class="row q-col-gutter-md q-mb-md">
              <div class="col-12 col-md-4">
                <div class="detail-box">
                  <div class="detail-label">Датум</div>
                  <div class="detail-value">{{ formatDate(selected.Datum) }}</div>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="detail-box">
                  <div class="detail-label">Фирма</div>
                  <div class="detail-value">{{ selected.NazivPartner || "" }}</div>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="detail-box">
                  <div class="detail-label">Барател</div>
                  <div class="detail-value">{{ selected.Baratel || "" }}</div>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="detail-box">
                  <div class="detail-label">Апликација</div>
                  <div class="detail-value">{{ selected.OpisAplikacija || "" }}</div>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="detail-box">
                  <div class="detail-label">Итност</div>
                  <div class="detail-value">{{ selected.StepenItnost || "" }}</div>
                </div>
              </div>

              <div class="col-12 col-md-4">
                <div class="detail-box">
                  <div class="detail-label">Статус</div>
                  <div class="detail-value">{{ statusiMap[selected.StatusID] || selected.StatusID }}</div>
                </div>
              </div>

              <div class="col-12">
                <div class="detail-box">
                  <div class="detail-label">Опис</div>
                  <div class="detail-value long-text">{{ selected.Opis || "" }}</div>
                </div>
              </div>
            </div>

            <q-card flat bordered class="q-pa-lg fazi-grid">
              <div class="text-subtitle1 text-mint text-bold q-mb-md">Додади следна фаза</div>

              <div class="row q-col-gutter-sm">
                <div class="col-12 col-md-2-4">
                  <q-select
                    class="fazi-small"
                    v-model="newFaza.fazaID"
                    :options="faziOptions"
                    :label="fazaErrors.fazaID ? 'Задолжително поле' : 'Фаза'"
                    outlined
                    dense
                    color="mint"
                    emit-value
                    map-options
                    hide-bottom-space
                    :class="{ 'req-field': fazaErrors.fazaID, 'required-field': !fazaErrors.fazaID }"
                    @update:model-value="fazaErrors.fazaID = false"
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-select
                    class="fazi-small"
                    v-model="newFaza.predal"
                    :options="[...employeeOptions]"
                    :label="fazaErrors.predal ? 'Задолжително поле' : 'Предал'"
                    outlined
                    dense
                    color="mint"
                    emit-value
                    map-options
                    hide-bottom-space
                    :class="{ 'req-field': fazaErrors.predal, 'required-field': !fazaErrors.predal }"
                    @update:model-value="fazaErrors.predal = false"
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-select
                    class="fazi-small"
                    v-model="newFaza.primil"
                    :options="employeeOptions"
                    label="Примил"
                    outlined
                    dense
                    color="mint"
                    emit-value
                    map-options
                    hide-bottom-space
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model="newFaza.predvidenDatum"
                    type="date"
                    label="Предвиден датум"
                    outlined
                    dense
                    color="mint"
                    hide-bottom-space
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model.number="newFaza.predvidenoVreme"
                    type="number"
                    step="0.25"
                    min="0"
                    label="Предвидено време"
                    outlined
                    dense
                    color="mint"
                    hide-bottom-space
                  />
                </div>
              </div>

              <div class="row q-col-gutter-sm q-mt-sm">
                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model="newFaza.start"
                    type="date"
                    label="Почеток"
                    outlined
                    dense
                    color="mint"
                    hide-bottom-space
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model="newFaza.end"
                    type="date"
                    label="Крај"
                    outlined
                    dense
                    color="mint"
                    hide-bottom-space
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model.number="newFaza.casovi"
                    type="number"
                    step="0.25"
                    min="0"
                    label="Часови"
                    outlined
                    dense
                    color="mint"
                    hide-bottom-space
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model.number="newFaza.efektivno"
                    type="number"
                    step="0.25"
                    min="0"
                    label="Ефективно"
                    outlined
                    dense
                    color="mint"
                    hide-bottom-space
                  />
                </div>

                <div class="col-12 col-md-2-4">
                  <q-input
                    class="fazi-small"
                    v-model="newFaza.zabeleska"
                    label="Забелешка"
                    outlined
                    dense
                    color="mint"
                    type="textarea"
                    autogrow
                    hide-bottom-space
                  />
                </div>
              </div>

              <div class="row justify-center q-mt-md">
                <q-btn
                  label="Додади фаза"
                  color="mint"
                  unelevated
                  rounded
                  no-caps
                  @click="dodadiFaza"
                />
              </div>
            </q-card>
          </q-card-section>
        </q-card>
      </q-dialog>

      <!-- POPUP Уредување -->
      <q-dialog v-model="editDialog.open">
        <q-card class="q-pa-lg small-popup" style="width: 640px; max-width: 95vw;">
          <div class="text-h6 text-mint text-bold q-mb-md">Уредување на фаза</div>

          <div class="row q-col-gutter-sm q-mb-sm">
            <div class="col-4">
              <q-select
                v-model="editDialog.data.fazaID"
                :options="faziOptions"
                :label="editErrors.fazaID ? 'Задолжително поле' : 'Фаза'"
                outlined dense color="mint"
                emit-value map-options hide-bottom-space
                :class="{ 'req-field': editErrors.fazaID, 'required-field': !editErrors.fazaID }"
                @update:model-value="editErrors.fazaID = false"
              />
            </div>

            <div class="col-4">
              <q-select
                v-model="editDialog.data.predal"
                :options="[...employeeOptions]"
                :label="editErrors.predal ? 'Задолжително поле' : 'Предал'"
                outlined dense color="mint"
                emit-value map-options hide-bottom-space
                :class="{ 'req-field': editErrors.predal, 'required-field': !editErrors.predal }"
                @update:model-value="editErrors.predal = false"
              />
            </div>

            <div class="col-4">
              <q-select
                v-model="editDialog.data.primil"
                :options="employeeOptions"
                label="Примил"
                outlined
                dense
                color="mint"
                emit-value
                map-options
              />
            </div>
          </div>

          <div class="row q-col-gutter-sm q-mb-sm">
            <div class="col-4">
              <q-input
                v-model="editDialog.data.predvidenDatum"
                type="date"
                label="Предвиден датум"
                outlined
                dense
                color="mint"
              />
            </div>

            <div class="col-4">
              <q-input
                v-model.number="editDialog.data.predvidenoVreme"
                type="number"
                step="0.25"
                min="0"
                label="Предвидено време"
                outlined
                dense
                color="mint"
              />
            </div>

            <div class="col-4">
              <q-input
                v-model="editDialog.data.start"
                type="date"
                label="Почеток"
                outlined
                dense
                color="mint"
              />
            </div>
          </div>

          <div class="row q-col-gutter-sm">
            <div class="col-4">
              <q-input
                v-model="editDialog.data.end"
                type="date"
                label="Крај"
                outlined
                dense
                color="mint"
              />
            </div>

            <div class="col-4">
              <q-input
                v-model.number="editDialog.data.casovi"
                type="number"
                step="0.25"
                min="0"
                label="Часови"
                outlined
                dense
                color="mint"
              />
            </div>

            <div class="col-4">
              <q-input
                v-model.number="editDialog.data.efektivno"
                type="number"
                step="0.25"
                min="0"
                label="Ефективно"
                outlined
                dense
                color="mint"
              />
            </div>
          </div>

          <div class="row q-col-gutter-sm q-mt-sm">
            <div class="col-12">
              <q-input
                v-model="editDialog.data.zabeleska"
                label="Забелешка"
                outlined
                dense
                color="mint"
                type="textarea"
                autogrow
              />
            </div>
          </div>

          <div class="row justify-center q-mt-md">
            <q-btn
              flat
              label="Откажи"
              color="grey"
              no-caps
              @click="editDialog.open = false"
            />
            <q-btn
              unelevated
              label="Зачувај"
              color="mint"
              no-caps
              class="q-ml-sm"
              @click="saveEditFaza"
            />
          </div>
        </q-card>
      </q-dialog>

      <!-- File Preview -->
      <q-dialog v-model="fileDialog.open" maximized>
        <q-card class="q-pa-md bg-white">
          <div class="row justify-end">
            <q-btn
              dense
              flat
              round
              icon="close"
              color="red"
              @click="fileDialog.open = false"
            />
          </div>

          <div v-if="fileDialog.type === 'image'" class="text-center">
            <img :src="fileDialog.url" style="max-width: 100%; max-height: 90vh;" />
          </div>

          <div v-else class="text-center q-pa-xl">
            <q-btn
              color="mint"
              icon="download"
              label="Симни документ"
              @click="downloadFile(fileDialog.url)"
            />
          </div>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script>
import api, { FILE_BASE } from "@/api";
import { Notify, Dialog } from "quasar";
import { getUser } from "@/services/db";
import { getStatusi } from "@/services/statusiService";
import { getVraboteniAPI } from "@/services/vraboteniAPI";
import { loadBaranjaAPI } from "@/services/baranjaAPI";
import { FaziColumns, PreglediGridColumns } from "@/services/forms/pregledi.js";
import { formatFazaDate, formatDate, formatBroj } from "../../utils/dateFormat";
import * as XLSX from "xlsx";

export default {
  name: "Pregledi",

  data() {
    return {
      mode: "pregledi",
      selected: {},
      user: getUser(),
      rows: [],
      tekovnaFazaMap: {},
      tekovenPrimilMap: {},
      loadingFazi: false,
      requestDialog: false,

      fileDialog: { open: false, url: "", type: "" },

      statusiMap: {},
      aplikaciiMap: {},
      firmiMap: {},

      newFaza: {
        start: "",
        end: "",
        primil: null,
        predal: null,
        casovi: "",
        predvidenDatum: "",
        predvidenoVreme: "",
        fazaID: null,
        efektivno: "",
        zabeleska: ""
      },

      fazaErrors: { fazaID: false, predal: false },
      editErrors: { fazaID: false, predal: false },

      editDialog: {
        open: false,
        id: null,
        data: {
          start: "",
          end: "",
          primil: null,
          predal: null,
          casovi: "",
          predvidenDatum: "",
          predvidenoVreme: "",
          fazaID: null,
          efektivno: "",
          vraboten: null,
          zabeleska: ""
        },
      },

      filtersDraft: { from: "", to: "", status: null, employee: null },
      filters: { from: "", to: "", status: null, employee: null },

      searchId: "",

      statusOptions: [],
      employeeOptions: [],
      employeeMap: {},
      faziOptions: [],
      faziMap: {},

      columns: PreglediGridColumns,
      faziColumns: FaziColumns,
    };
  },

  computed: {
    filtrirani() {
      if (this.loadingFazi) return [];
      const from = this.filters.from ? new Date(this.filters.from + "T00:00:00") : null;
      const to = this.filters.to ? new Date(this.filters.to + "T23:59:59") : null;
      const search = String(this.searchId || "").trim();

      return this.rows.filter((r) => {
        const d = this.parseBackendDate(r.Datum);

        const fromOk = !from || (d && d >= from);
        const toOk = !to || (d && d <= to);

        const statusOk = !this.filters.status || String(r.StatusID) === String(this.filters.status);
        const empOk = !this.filters.employee || String(r.vraboten) === String(this.filters.employee);
        const idOk = !search || String(r.ID).includes(search);

        const lastFazaId = Number(this.tekovnaFazaMap[r.ID] || 0);
        const noFazaOk = lastFazaId === 0;

        return fromOk && toOk && statusOk && empOk && idOk && noFazaOk;
      });
    },

    userFullName() {
      const u = this.user || {};
      const vrabId =
        u.VrabID ?? u.VrabotenID ?? u.id ?? u.SifraID ?? u.userId ?? u.username ?? u.ime ?? null;

      const key = vrabId == null ? "" : String(vrabId);
      return this.employeeMap[key] || "Систем";
    }
  },

  methods: {
    todayISO() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    setDefaultFazaDates() {
      if (!this.newFaza.predvidenDatum) this.newFaza.predvidenDatum = "";
      if (!this.newFaza.start) this.newFaza.start = "";
      if (!this.newFaza.end) this.newFaza.end = "";
    },

    formatFazaDate,
    formatDate,
    formatBroj,

    formatBrojID(id, len = 8) {
      if (id === null || id === undefined) return "";
      return String(id).padStart(len, "0");
    },

    shortText(text, max = 55) {
      if (!text) return "";
      return text.length > max ? `${text.slice(0, max)}...` : text;
    },

    async fetchLastFaza(baranjeId) {
      try {
        const res = await api.get("/baranja/get-faza", {
          params: { BaranjeID: baranjeId }
        });
        const data = res.data;
        const list = Array.isArray(data) ? data : [];
        const last = list.length ? list[list.length - 1] : null;
        return last;
      } catch (e) {
        console.error("fetchLastFaza error", e);
        return null;
      }
    },

    async hydrateTekovniFaziForAll() {
      this.tekovnaFazaMap = {};
      this.tekovenPrimilMap = {};

      await Promise.all(
        this.rows.map(async (r) => {
          const last = await this.fetchLastFaza(r.ID);
          this.$set(this.tekovnaFazaMap, r.ID, last?.FazaID || 0);
          this.$set(this.tekovenPrimilMap, r.ID, last?.PrimilVrabID || null);
        })
      );
    },

    formatOnlyDate(value) {
      if (!value) return "";
      const d = new Date(value);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    },

    onSearchIdInput(val) {
      const onlyDigits = String(val || "").replace(/\D/g, "").slice(0, 10);
      if (onlyDigits !== this.searchId) this.searchId = onlyDigits;
    },

    formatToDateOnly(value) {
      if (!value) return "";
      return value.split("T")[0];
    },

    parseBackendDate(value) {
      if (!value) return null;

      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
      }

      const str = String(value).trim();

      const direct = new Date(str);
      if (!Number.isNaN(direct.getTime())) {
        return direct;
      }

      if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        const isoDate = str.slice(0, 10);
        const parsedIso = new Date(`${isoDate}T00:00:00`);
        if (!Number.isNaN(parsedIso.getTime())) return parsedIso;
      }

      if (/^\d{2}\.\d{2}\.\d{4}$/.test(str)) {
        const [dd, mm, yyyy] = str.split(".");
        const parsedDots = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
        if (!Number.isNaN(parsedDots.getTime())) return parsedDots;
      }

      const parts = str.split(" ");
      const datePart = parts[0];
      const dashParts = datePart.split("-");

      if (dashParts.length === 3) {
        const [day, mon, year2] = dashParts;
        const months = {
          Jan: "01", Feb: "02", Mar: "03", Apr: "04",
          May: "05", Jun: "06", Jul: "07", Aug: "08",
          Sep: "09", Oct: "10", Nov: "11", Dec: "12"
        };

        if (months[mon]) {
          const yyyy = year2.length === 2 ? `20${year2}` : year2;
          const mm = months[mon];
          const parsedNamed = new Date(`${yyyy}-${mm}-${day}T00:00:00`);
          if (!Number.isNaN(parsedNamed.getTime())) return parsedNamed;
        }

        if (/^\d{4}$/.test(day) && /^\d{2}$/.test(mon) && /^\d{2}$/.test(year2)) {
          const parsedYmd = new Date(`${day}-${mon}-${year2}T00:00:00`);
          if (!Number.isNaN(parsedYmd.getTime())) return parsedYmd;
        }
      }

      return null;
    },

    toISODate(value) {
      if (!value) return "";
      if (String(value).includes("T")) return String(value).split("T")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);

      const parts = String(value).split(" ");
      const datePart = parts[0];
      const [day, mon, year2] = datePart.split("-");
      const months = {
        Jan: "01", Feb: "02", Mar: "03", Apr: "04",
        May: "05", Jun: "06", Jul: "07", Aug: "08",
        Sep: "09", Oct: "10", Nov: "11", Dec: "12"
      };
      const mm = months[mon];
      if (!mm) return "";
      const yyyy = year2.length === 2 ? `20${year2}` : year2;
      return `${yyyy}-${mm}-${day}`;
    },

    async load() {
      try {
        const apiData = await loadBaranjaAPI();
        const list = Array.isArray(apiData) ? apiData : (apiData?.Items || apiData?.List || []);

        this.rows = list.map((item) => ({
          ID: item.ID,
          PartnerID:
            item.PartnerID ??
            item.PartnerId ??
            item.FirmaID ??
            item.FirmaId ??
            item.KomintentID ??
            item.KomintentId ??
            item.Partner ??
            item.Firma ??
            null,

          Baratel: item.Baratel,
          Opis: item.Opis,
          Datum: item.Datum,
          NazivPartner: item.NazivPartner,
          OpisAplikacija: item.OpisAplikacija,
          AplikacijaID: item.AplikacijaID,
          StatusID: item.StatusID,
          StepenItnost: item.StepenItnost,
          Files: item.Files,
          vraboten: item.VrabIDCreate || item.VrabotenID || item.VrabID || null,
          fazi: [],
        }));

        this.loadingFazi = true;
        await this.hydrateTekovniFaziForAll();
        this.loadingFazi = false;

      } catch (err) {
        console.error("Грешка при вчитување барања:", err);
        this.rows = [];
        this.loadingFazi = false;
      }
    },

    async loadVraboteni() {
      try {
        const apiData = await getVraboteniAPI();

        this.employeeOptions = apiData.map((v) => ({
          label: v.Naziv,
          value: v.SifraID,
        }));

        this.employeeMap = Object.fromEntries(
          apiData.map((v) => [String(v.SifraID), v.Naziv])
        );

      } catch (err) {
        console.error("Грешка при вчитување вработени:", err);
      }
    },

    async loadStatusi() {
      try {
        const list = await getStatusi();
        this.statusOptions = list.map((s) => ({ label: s.Opis, value: s.SifraID }));
        this.statusiMap = Object.fromEntries(list.map((s) => [s.SifraID, s.Opis]));
      } catch (err) {
        console.error("Грешка при вчитување статуси:", err);
      }
    },

    async loadAplikacii() {
      try {
        const res = await api.get("/aplikacii");
        const data = res.data;
        this.aplikaciiMap = Object.fromEntries(data.map((a) => [a.SifraID, a.Opis]));
      } catch (err) {
        console.error("Грешка при вчитување апликации:", err);
      }
    },

    async loadFirmi() {
      try {
        const res = await api.get("/get/firmi");
        const data = res.data;
        this.firmiMap = Object.fromEntries(data.map(f => [String(f.SifraID), f.Naziv]));
      } catch (err) {
        console.error("Грешка при вчитување фирми:", err);
        this.firmiMap = {};
      }
    },

    async loadFazi() {
      try {
        const res = await api.get("/get/fazi");
        const data = res.data;
        this.faziOptions = data.map((f) => ({ label: f.Opis, value: f.SifraID }));
        this.faziMap = Object.fromEntries(data.map((f) => [f.SifraID, f.Opis]));
      } catch (err) {
        console.error("Грешка при вчитување фази:", err);
      }
    },

    async loadFaziForBaranje(BaranjeID) {
      try {
        const res = await api.get("/baranja/get-faza", {
          params: { BaranjeID }
        });
        const data = res.data;

        if (!Array.isArray(data)) {
          this.selected.fazi = [];
          return;
        }

        this.selected.fazi = data.map((f) => ({
          id: `${f.BaranjeID}-${f.FazaID}-${f.SySDatumCreate}`,
          pkid: f.pkid,
          start: this.toISODate(f.DatumPocetok),
          end: this.toISODate(f.DatumKraj),
          primil: f.PrimilVrabID || null,
          predal: f.PredalVrabID || null,
          casovi: f.Vreme ?? "",
          efektivno: f.Efektivno ?? "",
          predvidenDatum: this.toISODate(f.DatumPredviden),
          predvidenoVreme: f.VremePred ?? "",
          fazaID: f.FazaID || null,
          fazaOpis: this.faziMap[f.FazaID] || "",
          vraboten: f.VrabIDCreate || null,
          isSaved: true,
          Info: "2",
          zabeleska: f.Zabeleska || ""
        }));
      } catch (err) {
        console.error("Грешка при читање фази ", err);
        this.selected.fazi = [];
      }
    },

    openRequestPopup(evt, row) {
      const targetRow = row || evt;
      if (!targetRow) return;

      this.selected = { ...targetRow };
      if (!this.selected.fazi) this.$set(this.selected, "fazi", []);

      this.resetNewFaza();
      this.setDefaultFazaDates();
      this.requestDialog = true;
      this.loadFaziForBaranje(targetRow.ID);
    },

    otvoriFazi(row) {
      this.selected = row;
      if (!this.selected.fazi) this.$set(this.selected, "fazi", []);
      this.resetNewFaza();
      this.setDefaultFazaDates();
      this.requestDialog = true;
      this.loadFaziForBaranje(row.ID);
    },

    async dodadiFaza() {
      this.fazaErrors = { fazaID: false, predal: false };
      if (!this.newFaza.fazaID) this.fazaErrors.fazaID = true;
      if (!this.newFaza.predal) this.fazaErrors.predal = true;

      const hasErrors = this.fazaErrors.fazaID || this.fazaErrors.predal;
      if (hasErrors) {
        this.$q.notify({
          type: "negative",
          message: "Пополнете ги задолжителните полиња",
          position: "bottom",
          timeout: 2500
        });
        return;
      }

      const faza = {
        id: `${this.selected.ID}-${Date.now()}`,
        pkid: 0,
        start: this.newFaza.start || "",
        end: this.newFaza.end || "",
        primil: this.newFaza.primil || null,
        predal: this.newFaza.predal,
        casovi: this.newFaza.casovi || "",
        predvidenDatum: this.newFaza.predvidenDatum || "",
        predvidenoVreme: this.newFaza.predvidenoVreme ?? "",
        fazaID: this.newFaza.fazaID,
        fazaOpis: this.faziMap[this.newFaza.fazaID] || "",
        efektivno: this.newFaza.efektivno || "",
        zabeleska: this.newFaza.zabeleska || "",
        vraboten: this.user?.username || null,
        isSaved: false,
        Info: "1",
      };

      if (!this.selected.fazi) {
        this.$set(this.selected, "fazi", []);
      }

      this.selected.fazi.push(faza);

      try {
        await this.saveFazi();
        this.resetNewFaza();
        this.setDefaultFazaDates();
      } catch (err) {
        console.error("Грешка при додавање фаза:", err);
      }
    },

    urediFaza(faza) {
      this.editDialog.id = faza.id;

      this.editDialog.data = {
        pkid: faza.pkid ?? 0,
        start: this.toISODate(faza.start),
        end: this.toISODate(faza.end),
        predvidenDatum: this.toISODate(faza.predvidenDatum),
        primil: faza.primil ?? null,
        predal: faza.predal ?? null,
        casovi: faza.casovi ?? "",
        predvidenoVreme: faza.predvidenoVreme ?? "",
        fazaID: faza.fazaID ?? null,
        efektivno: faza.efektivno ?? "",
        vraboten: faza.vraboten ?? (this.user?.username || null),
        zabeleska: faza.zabeleska ?? "",
      };

      this.editErrors = { fazaID: false, predal: false };
      this.editDialog.open = true;
    },

    saveEditFaza() {
      this.editErrors = { fazaID: false, predal: false };

      const d = this.editDialog.data;
      if (!d.fazaID) this.editErrors.fazaID = true;
      if (!d.predal) this.editErrors.predal = true;

      if (this.editErrors.fazaID || this.editErrors.predal) {
        this.$q.notify({
          type: "negative",
          message: "Пополнете ги задолжителните полиња",
          position: "bottom",
          timeout: 2500
        });
        return;
      }

      const idx = this.selected.fazi.findIndex(f => f.id === this.editDialog.id);
      if (idx === -1) return;

      this.selected.fazi.splice(idx, 1, {
        id: this.editDialog.id,
        pkid: d.pkid,
        start: d.start || "",
        end: d.end || "",
        primil: d.primil || null,
        predal: d.predal,
        casovi: d.casovi || "",
        predvidenDatum: d.predvidenDatum || "",
        predvidenoVreme: d.predvidenoVreme ?? "",
        fazaID: d.fazaID,
        fazaOpis: this.faziMap[d.fazaID] || "",
        efektivno: d.efektivno || "",
        zabeleska: d.zabeleska || "",
        vraboten: d.vraboten || this.user?.username || null,
        isSaved: false,
        Info: "2",
      });

      this.$q.notify({
        color: "positive",
        message: "Фазата е успешно ажурирана!",
        position: "bottom",
        timeout: 2500
      });
      this.editDialog.open = false;
    },

    async izbrisiFaza(id) {
      const faza = this.selected.fazi.find(f => f.id === id);
      if (!faza) return;

      Dialog.create({
        title: "Потврда",
        message: "Дали сте сигурни дека сакате да ја избришете фазата?",
        cancel: { label: "Откажи", flat: true, color: "grey" },
        ok: { label: "Избриши", color: "red", unelevated: true },
        persistent: true
      })
      .onOk(async () => {
        try {
          if (faza.pkid) {
            await api.delete(`/baranja/delete-faza/${faza.pkid}`);
          }

          this.selected.fazi = this.selected.fazi.filter(f => f.id !== id);

          Notify.create({
            type: "positive",
            message: "Фазата е успешно избришана!",
            position: "bottom",
            timeout: 2500
          });

          if (this.editDialog.id === id) {
            this.editDialog.id = null;
            this.editDialog.open = false;
          }
        } catch (err) {
          console.error(err);
          Notify.create({
            type: "negative",
            message: "Грешка при бришење на фазата!",
            position: "bottom",
            timeout: 2500
          });
        }
      })
      .onCancel(() => {
        Notify.create({
          type: "warning",
          message: "Бришењето на фазата е откажано.",
          position: "bottom",
          timeout: 2500
        });
      });
    },

    async saveFazi() {
      try {
        if (!this.selected.fazi || !this.selected.fazi.length) {
          this.$q.notify({
            color: "info",
            message: "Нема фази за зачувување.",
            position: "bottom",
            timeout: 2500
          });
          return;
        }

        const invalid = this.selected.fazi.find(f => !f.fazaID);
        if (invalid) {
          this.$q.notify({
            color: "warning",
            message: "Избери Фаза за сите редови пред зачувување.",
            position: "bottom",
            timeout: 2500
          });
          return;
        }

        const payload = {
          CurrentUserId: Number(this.user?.id) || 0,
          Items: this.selected.fazi.map(f => ({
            pkid: Number(f.pkid) || 0,
            Info: (Number(f.pkid) || 0) > 0 ? 2 : 1,
            BaranjeID: this.selected.ID || 0,
            FazaID: Number(f.fazaID) || 0,
            PredalVrabID: f.predal ? Number(f.predal) : null,
            PrimilVrabID: f.primil ? Number(f.primil) : null,
            DatumPredviden: this.toISODate(f.predvidenDatum),
            DatumPocetok: this.toISODate(f.start),
            DatumKraj: this.toISODate(f.end),
            VremePred: f.predvidenoVreme === "" ? null : Number(f.predvidenoVreme),
            Vreme: f.casovi === "" ? null : Number(f.casovi),
            Efektivno: f.efektivno === "" ? null : Number(f.efektivno),
            Zabeleska: f.zabeleska || ""
          }))
        };

        let data;

        try {
          const res = await api.post("/baranja/save-fazi", payload, {
            headers: { "Content-Type": "application/json" }
          });

          data = res.data;
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.Message ||
            err?.message ||
            "Грешка при зачувување.";

          this.$q.notify({
            color: "negative",
            message: msg,
            position: "bottom",
            timeout: 3000
          });
          throw err;
        }

        if (Array.isArray(data) && data.length) {
          this.selected.fazi = this.selected.fazi.map(f => {
            const match = data.find(x =>
              String(x.BaranjeID) === String(this.selected.ID) &&
              String(x.FazaID) === String(f.fazaID) &&
              this.toISODate(x.DatumPocetok) === this.toISODate(f.start) &&
              this.toISODate(x.DatumKraj) === this.toISODate(f.end)
            );

            return match
              ? { ...f, pkid: Number(match.pkid) || f.pkid, Info: 2, isSaved: true }
              : { ...f, Info: 2, isSaved: true };
          });
        } else {
          this.selected.fazi = this.selected.fazi.map(f => ({ ...f, Info: 2, isSaved: true }));
        }

        const brojBaranje = this.formatBrojID(this.selected.ID);

        this.$q.notify({
          message: `Фазата со број ${brojBaranje} е успешно зачувана.`,
          position: "bottom",
          timeout: 3500,
          color: "white",
          textColor: "mint",
          icon: "check_circle",
          classes: "notify-faza-top"
        });

        this.rows = this.rows.filter(x => String(x.ID) !== String(this.selected.ID));
        this.requestDialog = false;

        const lastLocal = this.selected.fazi[this.selected.fazi.length - 1] || null;
        this.$set(this.tekovnaFazaMap, this.selected.ID, lastLocal?.fazaID || 1);
        this.$set(this.tekovenPrimilMap, this.selected.ID, lastLocal?.primil || null);

      } catch (err) {
        console.error("SAVE ERROR:", err);
        if (!err?.response) {
          this.$q.notify({
            color: "negative",
            message: "Грешка при зачувување на фазите!",
            position: "bottom",
            timeout: 2500
          });
        }
        throw err;
      }
    },

    resetNewFaza() {
      this.newFaza = {
        start: "",
        end: "",
        primil: null,
        predal: null,
        casovi: "",
        predvidenDatum: "",
        predvidenoVreme: "",
        fazaID: null,
        efektivno: "",
        zabeleska: ""
      };

      this.fazaErrors = { fazaID: false, predal: false };
    },

    previewFile(filePath) {
      const fullUrl = FILE_BASE + filePath;

      const isImg = /\.(png|jpg|jpeg|gif|webp)$/i.test(filePath);
      const isDoc = /\.(pdf|txt|doc|docx|xlsx)$/i.test(filePath);

      if (isImg) {
        this.fileDialog.url = fullUrl;
        this.fileDialog.type = "image";
        this.fileDialog.open = true;
      } else if (isDoc) {
        this.downloadFile(fullUrl);
      } else {
        window.open(fullUrl, "_blank");
      }
    },

    downloadFile(url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = url.split("/").pop();
      a.click();
    },

    primeniFiltri() {
      this.filters = { ...this.filtersDraft };
      this.$q.notify({
        color: "positive",
        message: "Филтрите се применети.",
        position: "bottom",
        timeout: 2000
      });
    },

    resetFiltri() {
      this.filtersDraft = { from: "", to: "", status: null, employee: null };
      this.filters = { from: "", to: "", status: null, employee: null };
      this.searchId = "";
      this.$q.notify({
        color: "grey-8",
        message: "Филтрите се исчистени.",
        position: "bottom",
        timeout: 2000
      });
    },

    exportExcel() {
      if (!this.filtrirani.length) {
        this.$q.notify({
          color: "warning",
          message: "Нема податоци.",
          position: "bottom",
          timeout: 2000
        });
        return;
      }

      const data = this.filtrirani.map((r) => ({
        "Број": this.formatBrojID(r.ID),
        "Фирма": r.NazivPartner,
        "Барател": r.Baratel,
        "Опис": r.Opis,
        "Датум": this.formatDate(r.Datum),
        "Апликација": r.OpisAplikacija,
        "Итност": r.StepenItnost,
        "Статус": this.statusiMap[r.StatusID] || r.StatusID,
      }));

      const sheet = XLSX.utils.json_to_sheet(data);

      const colWidths = Object.keys(data[0]).map((key) => ({
        wch: Math.max(key.length, ...data.map((row) => String(row[key]).length)) + 2,
      }));

      sheet["!cols"] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "Baranja");
      XLSX.writeFile(wb, "Baranja.xlsx");

      this.$q.notify({
        color: "positive",
        message: "Excel датотеката е успешно симната!",
        position: "bottom",
        timeout: 2500
      });
    },
  },

  async mounted() {
    this.user = getUser();

    await this.loadAplikacii();
    await this.loadStatusi();
    await this.loadVraboteni();
    await this.loadFazi();
    await this.loadFirmi();
    await this.load();
  },
};
</script>

<style scoped>
.req-field ::v-deep .q-field__label {
  color: #c10015 !important;
  font-size: 11px !important;
  font-weight: 400 !important;
  letter-spacing: 0.1px;
}

.col-md-2-4 { flex: 0 0 20%; max-width: 20%; }

@media (max-width: 1023px) {
  .col-md-2-4 { flex: 0 0 50%; max-width: 50%; }
}
@media (max-width: 599px) {
  .col-md-2-4 { flex: 0 0 100%; max-width: 100%; }
}

.fazi-grid .q-field { width: 100%; }

.req-field ::v-deep .q-field--float .q-field__label {
  font-size: 10.5px !important;
  font-weight: 400 !important;
}

.req-field ::v-deep .q-field__control { border-radius: 10px; }

.bg-page { background-color: #f7faf9; min-height: 100vh; }
.container-xl { max-width: 1180px; margin: 0 auto; }
.text-mint { color: #0e6b5f; }

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.info-strip {
  background: white;
  border: 1px solid #d6ebe6;
  border-radius: 12px;
  padding: 10px 18px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  color: #0e6b5f;
}

.info-item { display: flex; align-items: center; gap: 6px; }
.filter-card { background: white; border-radius: 14px; }
.table-card { background: white; border-radius: 14px; }

.q-table thead tr {
  background-color: #f1f6f5;
  color: #0e6b5f;
  font-weight: 600;
}

.q-table tbody tr:hover {
  background-color: #f6fdfb;
  transition: 0.2s ease;
}

.filter-btn {
  min-width: 130px;
  height: 38px;
  font-weight: 600;
  letter-spacing: 0.3px;
  margin: 0 !important;
  padding: 0 16px !important;
}

.fazi-small .q-field__control { min-height: 30px !important; height: 30px !important; }

.fazi-small .q-field__native,
.fazi-small input {
  height: 30px !important;
  padding: 0 6px !important;
  font-size: 12px !important;
}

.fazi-small .q-field__label {
  font-size: 11px !important;
  top: 4px !important;
}

.fazi-small .q-select__dropdown-icon {
  font-size: 16px !important;
  margin-top: -6px;
}

.spin-fix ::v-deep input[type="number"]::-webkit-inner-spin-button,
.spin-fix ::v-deep input[type="number"]::-webkit-outer-spin-button {
  margin-top: -3px !important;
}

.save-fazi-btn {
  font-weight: 600;
  height: 40px;
  border-radius: 10px;
}

.close-top-right {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

.fazi-akcija-td { position: relative; }

.fazi-status-dot {
  position: absolute;
  top: 4px;
  right: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid #ffffff;
}

.fazi-status-dot.saved { background-color: #0e6b5f; }
.fazi-status-dot.unsaved { background-color: #f2c037; }

.zabeleska-red { color: #c10015 !important; }

.btn-filter{
  height: 38px !important;
  min-height: 38px !important;
  padding: 0 20px !important;
  font-size: 13px !important;
  font-weight: 600;
  border-radius: 10px !important;
}

.btn-filter-outline{
  border: 1px solid #0e6b5f !important;
}

.required-field ::v-deep .q-field__label::after {
  content: " *";
  color: #c10015;
  font-weight: 700;
}

.notify-faza-top {
  background: #ffffff !important;
  color: #0e6b5f !important;
  border: 1px solid #cfe7e2;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(14, 107, 95, 0.10);
  font-weight: 600;
}

.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.opis-preview {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.request-dialog-card {
  width: 1200px;
  max-width: 97vw;
  border-radius: 18px;
  overflow: hidden;
}

.dialog-header {
  background: #ffffff;
}

.dialog-content {
  max-height: 82vh;
  overflow-y: auto;
}

.detail-box {
  background: #f9fbfb;
  border: 1px solid #e7eeee;
  border-radius: 10px;
  padding: 12px;
}

.detail-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 6px;
}

.detail-value {
  font-size: 14px;
  color: #1f2937;
  word-break: break-word;
}

.long-text {
  white-space: pre-wrap;
  line-height: 1.6;
}
</style>